/**
 * Shared orchestration for multi-document tabs: every call site that loads,
 * switches, closes, or renames the active document funnels through here
 * instead of separately touching documentStore/highlightStore/tabsStore/fs.
 *
 * The app only ever keeps one document's content live in the editor at a
 * time (see Editor.tsx) — switching tabs always flushes the outgoing
 * document first, then loads the target from disk. There's no cached
 * in-memory copy of a background tab to reuse.
 */

import { fs } from "../data/fs";
import { useDocumentStore } from "../store/documentStore";
import { useHighlightStore } from "../store/highlightStore";
import { useTabsStore } from "../store/tabsStore";
import { useProjectStore } from "../store/projectStore";
import { useEditorStore } from "./editorStore";
import { flushAutosave, cancelPendingAutosave } from "./autosave";
import { loadHighlightsForDocument, markLoadedFromDisk } from "./persist";
import { paragraphsToPmDoc } from "./convert";
import { getEditorSchema } from "./schema";
import { syncHighlightDecorations } from "./highlightPlugin";
import { getCollabSession } from "../collab/session";
import { QdaDocument, Highlight } from "../types";
import { now } from "../utils/idGenerator";
import { isSlug } from "../utils/slug";
import { assertFileIdAvailable, listDocumentSummaries } from "../data/documents";
import { clearCachedProjectKey } from "../data/projects";

async function persistWorkingState(projectName: string): Promise<void> {
  const { openTabs, activeId } = useTabsStore.getState();
  await fs.setWorkingReference(projectName, activeId, openTabs.map((t) => t.id));
}

/**
 * Puts an already-in-memory document into the live slot, ensures it's an
 * open+active tab, and persists. Caller is responsible for having already
 * written doc/highlights to disk if needed and for flushing any outgoing
 * document beforehand.
 */
export async function applyActiveDocument(
  projectName: string,
  doc: QdaDocument,
  highlights: Highlight[]
): Promise<void> {
  // Record this disk state as "already saved" before touching the stores:
  // setHighlights below fires the autosave subscription, and without this seed
  // it would rewrite highlights.json on every open, bumping its mtime and
  // breaking cloud sync's newer-wins download. See persist.markLoadedFromDisk.
  markLoadedFromDisk(highlights);
  useDocumentStore.getState().setDocument(doc);
  useHighlightStore.getState().setHighlights(highlights);
  useTabsStore.getState().openTab(doc.id, doc.name);
  await persistWorkingState(projectName);
}

/**
 * Re-reads the active document + highlights from disk into the live editor,
 * WITHOUT flushing in-memory edits first. Used after cloud sync downloads a
 * newer remote copy of the open document (see qdaSync.syncNow): the download
 * already won the newer-wins comparison, so disk is authoritative and we must
 * not write the stale in-memory copy back over it — hence cancelPendingAutosave
 * rather than flushAutosave. No-op unless `docName` is the active document.
 *
 * Editor.tsx's content-load effect keys on document.id and deliberately skips
 * same-id reloads (persist updates the store constantly), so we can't rely on
 * it to refresh the view — we push the new content into the live editor here.
 *
 * Collaboration coexistence: when a live Yjs session is running for this
 * document, its shared state is authoritative for the open document, not the
 * disk snapshot. If other collaborators are connected we leave the live editor
 * untouched (they already merged the truth over WebRTC). If we're the only peer
 * (the "download from another device that was offline" case), we reseed the
 * live Y.Doc from the newer snapshot instead of setContent (which would fight
 * the Collaboration binding).
 */
export async function reloadActiveDocumentFromDisk(
  projectName: string,
  fileId: string
): Promise<void> {
  if (useTabsStore.getState().activeId !== fileId) return;
  const doc = (await fs.readFile(
    fs.buildPath(projectName, fileId, "document.json")
  )) as QdaDocument | null;
  if (!doc) return;
  let highlights: Highlight[] = [];
  try {
    highlights = await loadHighlightsForDocument(projectName, fileId);
  } catch (err) {
    console.warn("[tabs] failed to load highlights on reload:", err);
  }

  const session = getCollabSession(fileId);
  if (session) {
    // Other collaborators are live: their merged Yjs state wins, don't clobber it.
    if (session.hasPeers()) return;
    // Sole peer: adopt the newer snapshot. With a lineage-sharing ystate this
    // is a true CRDT merge into the live Y.Doc; otherwise a clear+reseed.
    cancelPendingAutosave();
    markLoadedFromDisk(highlights);
    useDocumentStore.getState().setDocument(doc);
    useHighlightStore.getState().setHighlights(highlights);
    const content = doc.content ?? paragraphsToPmDoc(doc.paragraphs, getEditorSchema()).toJSON();
    session.reseed(content, doc.ystate ?? null);
    return;
  }

  // No live session (e.g. still connecting): fall back to the legacy setContent
  // path if a plain editor happens to be mounted, else just refresh the stores.
  cancelPendingAutosave();
  markLoadedFromDisk(highlights);
  useDocumentStore.getState().setDocument(doc);
  useHighlightStore.getState().setHighlights(highlights);
  const editor = useEditorStore.getState().editor;
  if (editor && !editor.isDestroyed) {
    const content = doc.content ?? paragraphsToPmDoc(doc.paragraphs, editor.schema).toJSON();
    editor.commands.setContent(content, false); // emitUpdate=false: don't reschedule a save
    syncHighlightDecorations(editor.view, highlights);
  }
}

/**
 * Flushes the outgoing document (no-op if nothing pending), reads `docName`
 * from disk, and activates it. Adds it as a tab if not already open.
 * Throws if it has no document.json on disk.
 */
export async function switchToDocument(projectName: string, fileId: string): Promise<void> {
  await flushAutosave();
  const doc = (await fs.readFile(fs.buildPath(projectName, fileId, "document.json"))) as
    | QdaDocument
    | null;
  if (!doc) throw new Error(`Could not load document "${fileId}".`);
  let highlights: Highlight[] = [];
  try {
    highlights = await loadHighlightsForDocument(projectName, fileId);
  } catch (err) {
    console.warn("[tabs] failed to load persisted highlights:", err);
  }
  await applyActiveDocument(projectName, doc, highlights);
}

/**
 * Creates a blank document, writes it to disk, and activates it. Shared by
 * MenuBar's "New..." and the document-chooser's "New Document" action.
 * Throws (leaving the caller's dialog open) if the name or file id is taken,
 * or the file id isn't a valid slug.
 */
export async function createNewDocument(
  projectName: string,
  name: string,
  fileId: string
): Promise<void> {
  if (!isSlug(fileId)) {
    throw new Error("File id must be lowercase letters, numbers, and hyphens.");
  }
  const docPath = fs.buildPath(projectName, fileId, "document.json");
  if (await fs.exists(docPath)) {
    throw new Error(`A document with id "${fileId}" already exists.`);
  }
  await assertFileIdAvailable(projectName, fileId);
  await flushAutosave();
  const blank: QdaDocument = {
    id: fileId,
    projectId: projectName,
    name,
    importedAt: now(),
    format: "docx",
    paragraphs: [],
  };
  await fs.writeFile(docPath, blank);
  await applyActiveDocument(projectName, blank, []);
}

/** Closes a tab (by fileId). Never touches the document on disk. */
export async function closeTab(projectName: string, fileId: string): Promise<void> {
  const { openTabs, activeId } = useTabsStore.getState();
  const closingActive = fileId === activeId;
  const index = openTabs.findIndex((t) => t.id === fileId);
  const remaining = openTabs.filter((t) => t.id !== fileId);

  if (closingActive) await flushAutosave();

  if (!closingActive) {
    useTabsStore.getState().closeTab(fileId);
    await persistWorkingState(projectName);
    return;
  }

  if (remaining.length === 0) {
    useTabsStore.getState().clear();
    useDocumentStore.getState().clear();
    useHighlightStore.getState().clear();
    await fs.setWorkingReference(projectName, null, []);
    return;
  }

  const neighbor = remaining[Math.min(Math.max(0, index - 1), remaining.length - 1)];
  useTabsStore.getState().setTabs(remaining, neighbor.id);
  await switchToDocument(projectName, neighbor.id);
}

/**
 * Save As semantics: swaps the active tab's identity to the newly-minted
 * document (new fileId + name) in place instead of adding a second tab. Caller
 * has already written the new doc/highlights to disk and updated the stores.
 */
export async function renameActiveTab(
  projectName: string,
  oldFileId: string,
  newFileId: string,
  newName: string
): Promise<void> {
  useTabsStore.getState().replaceTab(oldFileId, newFileId, newName);
  await persistWorkingState(projectName);
}

/**
 * Locks the current project — drops its cached key so the password is
 * required again — without forgetting which project was active. Used by
 * "Logout".
 */
export async function lockProject(): Promise<void> {
  await flushAutosave();
  useDocumentStore.getState().clear();
  useHighlightStore.getState().clear();
  useTabsStore.getState().clear();
  clearCachedProjectKey();
  useProjectStore.getState().setProjectKey(null);
}

/**
 * Leaves the current project entirely, returning to the full projects list.
 * Used by "Switch Project".
 */
export async function switchProject(): Promise<void> {
  await flushAutosave();
  useDocumentStore.getState().clear();
  useHighlightStore.getState().clear();
  useTabsStore.getState().clear();
  clearCachedProjectKey();
  useProjectStore.getState().reset();
}

/**
 * Boot restore: filters the persisted tab list against what actually exists
 * on disk, loads the active doc, and self-heals the persisted state if it
 * had to correct anything. No-op (stores stay empty, the document chooser
 * shows) if nothing valid survives.
 */
export async function restoreTabsOnBoot(projectName: string): Promise<void> {
  const { activeId: savedActive, openIds: savedOpen } = await fs.getWorkingReference(projectName);
  // Resolve each surviving fileId to a {id, name} tab (name for the label).
  const byId = new Map((await listDocumentSummaries(projectName)).map((s) => [s.id, s]));
  const openTabs = savedOpen.filter((id) => byId.has(id)).map((id) => byId.get(id)!);
  if (openTabs.length === 0) return;

  const activeId =
    savedActive && openTabs.some((t) => t.id === savedActive) ? savedActive : openTabs[0].id;
  useTabsStore.getState().setTabs(openTabs, activeId);
  await switchToDocument(projectName, activeId);

  if (openTabs.length !== savedOpen.length || activeId !== savedActive) {
    await persistWorkingState(projectName);
  }
}
